import { useEffect, useState } from "react";

// Shared live data for the Jakarta WatchGuard panels (GET /api/net/firebox/status, polled every 5 min).
// Returns { live, checkedAt, error } where live = last good SNMP snapshot { active, sas[], ifs[] } or null.
const API_BASE = window.__API_BASE__ || "";
let cache = null, inflight = null, last = 0;
const subs = new Set();

async function fetchNow() {
  if (inflight) return inflight;
  inflight = fetch(API_BASE + "/api/net/firebox/status", { headers: { Authorization: "Bearer " + (localStorage.getItem("auth_token") || "") } })
    .then((r) => (r.ok ? r.json() : Promise.reject(new Error("HTTP " + r.status))))
    .then((j) => {
      const g = j.lastGood || null;
      cache = { live: g, prev: j.prevGood || null, checkedAt: g ? g.checked_at : null, error: j.latest && j.latest.error ? j.latest.error : null, history: j.history || [] };
    })
    .catch((e) => { cache = { live: null, prev: null, checkedAt: null, error: e.message, history: [] }; })
    .finally(() => { inflight = null; last = Date.now(); subs.forEach((f) => f(cache)); });
  return inflight;
}

export default function useFireboxLive() {
  const [s, setS] = useState(cache || { live: null, prev: null, checkedAt: null, error: null, history: [] });
  useEffect(() => {
    subs.add(setS);
    if (!cache || Date.now() - last > 60000) fetchNow();
    const t = setInterval(fetchNow, 5 * 60 * 1000);
    return () => { subs.delete(setS); clearInterval(t); };
  }, []);
  return s;
}

// Helpers shared by the panels
export const norm = (c) => String(c || "").replace(/\/32$/, "");
export const saKey = (local, remote) => norm(local) + ">" + norm(remote);
export const wib = (s) => {
  if (!s) return "-";
  const d = new Date(String(s).replace(" ", "T") + "Z");
  return d.toLocaleString("en-GB", { timeZone: "Asia/Jakarta", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) + " WIB";
};
export const ageMin = (s) => (s ? (Date.now() - new Date(String(s).replace(" ", "T") + "Z").getTime()) / 60000 : Infinity);

const ts = (s) => new Date(String(s).replace(" ", "T") + "Z").getTime();
// Per-port throughput (Mbps) from the two latest good snapshots: { [eth]: { inMbps, outMbps } }
export function rates(live, prev) {
  const out = {};
  if (!live || !prev || !live.ifs || !prev.ifs) return out;
  const dt = (ts(live.checked_at) - ts(prev.checked_at)) / 1000;
  if (!(dt > 30)) return out;
  for (const f of live.ifs) {
    const p = prev.ifs.find((x) => x.eth === f.eth);
    if (!p) continue;
    const d = (a, b) => (a != null && b != null && a >= b ? ((a - b) * 8) / dt / 1e6 : null);
    out[f.eth] = { inMbps: d(f.in, p.in), outMbps: d(f.out, p.out) };
  }
  return out;
}
export const mbps = (v) => (v == null ? "-" : v >= 100 ? v.toFixed(0) + " Mbps" : v >= 1 ? v.toFixed(1) + " Mbps" : (v * 1000).toFixed(0) + " Kbps");
export const bytes = (b) => { if (b == null) return "-"; const u = ["B", "KB", "MB", "GB", "TB"]; let i = 0, v = Number(b); while (v >= 1000 && i < 4) { v /= 1000; i++; } return v.toFixed(i ? 1 : 0) + " " + u[i]; };
export const uptimeText = (cs) => { if (cs == null) return null; let s = Math.floor(Number(cs) / 100); const d = Math.floor(s / 86400); s -= d * 86400; const h = Math.floor(s / 3600); const m = Math.floor((s % 3600) / 60); return `${d} days ${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`; };
